import { useState, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileArrowUp, Check, X, ArrowsClockwise, Eye, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IngredientFileImporter({ open, onClose, onImported }) {
  const [step, setStep] = useState("upload"); // upload | analyzing | preview | importing
  const [fileName, setFileName] = useState("");
  const [parsedIngredients, setParsedIngredients] = useState([]);
  const [summary, setSummary] = useState("");
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep("upload");
    setFileName("");
    setParsedIngredients([]);
    setSummary("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const getFileType = (name) => {
    const ext = name.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "webp", "heic"].includes(ext)) return "image";
    if (["xlsx", "xls", "xlsm"].includes(ext)) return "excel";
    if (ext === "pdf") return "pdf";
    if (ext === "csv") return "csv";
    return "image";
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setStep("analyzing");

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      const fileType = getFileType(file.name);

      try {
        const { data } = await axios.post(`${API}/ingredients/import-file`, {
          file_base64: base64,
          file_type: fileType,
          file_name: file.name,
        });

        const ings = (data.ingredientes || []).map((ing, idx) => ({
          ...ing,
          selected: true,
          action: ing.status === "exists" ? "skip" : ing.status === "similar" ? "update" : "create",
          _idx: idx,
        }));

        setParsedIngredients(ings);
        setSummary(data.resumo || `${ings.length} ingredientes encontrados`);
        setStep("preview");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro ao analisar arquivo");
        setStep("upload");
      }
    };
    reader.readAsDataURL(file);
  };

  const toggleIngredient = (idx) => {
    setParsedIngredients((prev) =>
      prev.map((ing) =>
        ing._idx === idx ? { ...ing, selected: !ing.selected } : ing
      )
    );
  };

  const setAction = (idx, action) => {
    setParsedIngredients((prev) =>
      prev.map((ing) => (ing._idx === idx ? { ...ing, action } : ing))
    );
  };

  const handleConfirmImport = async () => {
    const toImport = parsedIngredients
      .filter((i) => i.selected && i.action !== "skip")
      .map((i) => ({
        nome: i.nome,
        unidade: i.unidade,
        quantidade_compra: i.quantidade_compra,
        preco_compra: i.preco_compra,
        categoria: i.categoria,
        action: i.action,
        existing_id: i.existing_id,
      }));

    if (toImport.length === 0) {
      toast.error("Nenhum ingrediente selecionado para importar");
      return;
    }

    setStep("importing");
    try {
      const { data } = await axios.post(`${API}/ingredients/import-confirm`, toImport);
      toast.success(`${data.created} criados, ${data.updated} atualizados!`);
      onImported?.();
      handleClose();
    } catch (err) {
      toast.error("Erro ao importar ingredientes");
      setStep("preview");
    }
  };

  const newCount = parsedIngredients.filter((i) => i.selected && i.action === "create").length;
  const updateCount = parsedIngredients.filter((i) => i.selected && i.action === "update").length;
  const skipCount = parsedIngredients.filter((i) => !i.selected || i.action === "skip").length;

  const statusColors = {
    new: "text-emerald-600 bg-emerald-50",
    exists: "text-amber-600 bg-amber-50",
    similar: "text-blue-600 bg-blue-50",
  };

  const statusLabels = {
    new: "Novo",
    exists: "Já existe",
    similar: "Similar",
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-[800px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="ingredient-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold tracking-tight flex items-center gap-2">
            <FileArrowUp size={22} />
            Importar Ingredientes de Arquivo
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="w-24 h-24 rounded-2xl bg-zinc-100 flex items-center justify-center">
              <FileArrowUp size={48} className="text-zinc-400" />
            </div>
            <p className="text-sm text-zinc-500 text-center max-w-sm">
              Envie um arquivo com lista de ingredientes e precos. O Sous-Chef IA vai analisar e extrair tudo automaticamente.
            </p>
            <p className="text-xs text-zinc-400">
              Formatos aceitos: PDF, Excel (.xlsx/.xls/.xlsm), CSV, Imagem
            </p>
            <Button
              data-testid="import-file-select-btn"
              onClick={() => fileInputRef.current?.click()}
              className="bg-zinc-950 text-white hover:bg-zinc-800"
            >
              <FileArrowUp size={18} className="mr-2" />
              Selecionar Arquivo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.xlsx,.xls,.xlsm,.csv,.jpg,.jpeg,.png,.webp"
              onChange={handleFileSelect}
            />
          </div>
        )}

        {step === "analyzing" && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <SpinnerGap size={48} className="text-zinc-400 animate-spin" />
            <p className="text-sm text-zinc-500">
              Sous-Chef IA analisando <strong>{fileName}</strong>...
            </p>
            <p className="text-xs text-zinc-400">Isso pode levar alguns segundos</p>
          </div>
        )}

        {step === "preview" && (
          <div className="flex flex-col gap-3 overflow-hidden">
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-zinc-600">{summary}</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{newCount} novos</span>
                <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">{updateCount} atualizar</span>
                <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-500">{skipCount} ignorar</span>
              </div>
            </div>

            <div className="overflow-auto max-h-[50vh] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Ingrediente</TableHead>
                    <TableHead>Un.</TableHead>
                    <TableHead>Qtd</TableHead>
                    <TableHead>Preco</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Acao</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedIngredients.map((ing) => (
                    <TableRow
                      key={ing._idx}
                      className={!ing.selected ? "opacity-40" : ""}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={ing.selected}
                          onChange={() => toggleIngredient(ing._idx)}
                          className="rounded"
                        />
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {ing.nome}
                        {ing.existing_name && (
                          <span className="block text-xs text-blue-500">
                            Similar a: {ing.existing_name}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{ing.unidade}</TableCell>
                      <TableCell className="text-xs">{ing.quantidade_compra}</TableCell>
                      <TableCell className="text-xs">
                        {ing.preco_compra > 0
                          ? `R$ ${Number(ing.preco_compra).toFixed(2)}`
                          : "-"}
                      </TableCell>
                      <TableCell className="text-xs">{ing.categoria}</TableCell>
                      <TableCell>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${statusColors[ing.status] || ""}`}
                        >
                          {statusLabels[ing.status] || ing.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={ing.action}
                          onValueChange={(v) => setAction(ing._idx, v)}
                        >
                          <SelectTrigger className="h-7 text-xs w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create">Criar</SelectItem>
                            <SelectItem value="update">Atualizar</SelectItem>
                            <SelectItem value="skip">Ignorar</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={reset} data-testid="import-back-btn">
                Voltar
              </Button>
              <Button
                onClick={handleConfirmImport}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="import-confirm-btn"
              >
                <Check size={18} className="mr-2" />
                Importar {newCount + updateCount} ingredientes
              </Button>
            </div>
          </div>
        )}

        {step === "importing" && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <SpinnerGap size={48} className="text-emerald-500 animate-spin" />
            <p className="text-sm text-zinc-500">Importando ingredientes...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
